import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  CreateCardRequest,
  UpdateCardRequest,
  CreateSensorRequest,
} from "@opus-dt/shared-types";
import type { CardSummary } from "../../types/cards.types.js";

export type { CardSummary, CardType } from "../../types/cards.types.js";

const cardsKey = (deviceId: string) => ["cards", deviceId] as const;

export function useCards(deviceId: string, options?: { enabled?: boolean }) {
  return useQuery<CardSummary[]>({
    queryKey: cardsKey(deviceId),
    queryFn: () => api.get<CardSummary[]>(`/api/v1/devices/${deviceId}/cards`),
    // Carga preguiçosa por nó da árvore: só busca quando o CLP é expandido.
    enabled: (options?.enabled ?? true) && Boolean(deviceId),
  });
}

export function useCreateCard(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCardRequest) =>
      api.post<CardSummary>(`/api/v1/devices/${deviceId}/cards`, data),
    meta: { skipErrorToast: true },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardsKey(deviceId) });
      // `["devices"]` (prefixo) cobre a lista (cardCount no badge da árvore) + o detalhe.
      void qc.invalidateQueries({ queryKey: ["devices"] });
    },
  });
}

export function useUpdateCard(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCardRequest }) =>
      api.patch<CardSummary>(`/api/v1/cards/${id}`, data),
    meta: { skipErrorToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: cardsKey(deviceId) }),
  });
}

export function useDeleteCard(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/cards/${id}`),
    meta: { skipErrorToast: true },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardsKey(deviceId) });
      void qc.invalidateQueries({ queryKey: ["devices"] });
    },
  });
}

// Sensor-posição: reusa o endpoint de sensor (POST /sensors com cardId+position).
export function useCreateSensorPosition(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSensorRequest) => api.post("/api/v1/sensors", data),
    meta: { skipErrorToast: true },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardsKey(deviceId) });
      void qc.invalidateQueries({ queryKey: ["devices", deviceId] });
      void qc.invalidateQueries({ queryKey: ["sensors"] });
    },
  });
}
